import type {
  Column,
  ColumnGroupOpenedEvent,
  ModelUpdatedEvent,
} from 'ag-grid-community'

/**
 * Auto-size a new column-state context only after its first rows render.
 *
 * The context-key guard prevents a late event from a previous layer or column
 * structure from resizing the currently active grid.
 */
export const autoSizeInitialColumns = (
  { api }: ModelUpdatedEvent,
  pendingContextKey: string | null,
  currentContextKey: string | null
): boolean => {
  if (!currentContextKey || pendingContextKey !== currentContextKey)
    return false
  if (!api.getRenderedNodes().some(node => node.data)) return false

  api.autoSizeAllColumns()
  return true
}

/**
 * Auto-size only the columns revealed by an expanded attribute-tab group.
 *
 * AG Grid does not include collapsed `columnGroupShow: 'open'` columns in its
 * initial auto-size pass. Sizing on the group-open event gives those newly
 * displayed columns a content-based width without reflowing unrelated columns.
 */
export const autoSizeExpandedColumnGroups = ({
  api,
  columnGroup,
  columnGroups,
}: ColumnGroupOpenedEvent): void => {
  const changedGroups = columnGroup ? [columnGroup] : columnGroups
  const columnsToAutoSize = new Set<Column>()

  changedGroups.forEach(group => {
    if (!group.isExpanded()) return
    group.getLeafColumns().forEach(column => {
      if (column.getColDef().columnGroupShow === 'open') {
        columnsToAutoSize.add(column)
      }
    })
  })

  if (columnsToAutoSize.size === 0) return
  api.autoSizeColumns([...columnsToAutoSize])
}
